import DropDownMultiSelector from 'components/common/dropDownMultiSelector';
import Paging from 'components/common/paging';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getToken } from 'context/auth';
import { getBookingsForParkingArea } from 'api/bookings';
import WidgetLoadingSkeleton from 'components/dashboard/widgetLoadingSkeleton';
import { useTranslation } from 'react-i18next';
import Widget from 'components/dashboard/widget';
import InputSearch from 'components/common/inputSearch';
import debounce from 'lodash/debounce';

const PAGE_SIZE = 10;

const BookingsPage = () => {
    const { parkingAreaExtId } = useParams();
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();

    // FIX 6: Lazy initial state — läs djup-länk-params direkt vid mount så vi inte triggar
    // en extra fetch via en separat useEffect.
    const initialSearch = useMemo(() => (
        searchParams.get('bookingExtId') || searchParams.get('discountCode') || ''
    ), [searchParams]);
    const initialIncludeExpired = useMemo(() => (
        Boolean(searchParams.get('bookingExtId') || searchParams.get('discountCode'))
    ), [searchParams]);

    const [pagedResult, setPagedResult] = useState({ items: [], totalPages: 0, totalCount: 0 });
    const [currentPage, setCurrentPage] = useState(0); // 0-baserat för Paging-komponenten
    const [searchInputValue, setSearchInputValue] = useState(initialSearch);
    const [searchText, setSearchText] = useState(initialSearch);
    const [includeExpired, setIncludeExpired] = useState(initialIncludeExpired);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [isRefetching, setIsRefetching] = useState(false);

    const [filterBookingOptions, setFilterBookingOptions] = useState(() => [
        { title: t('bookingsPage.expired'), value: 'expired', isChecked: initialIncludeExpired },
    ]);

    const abortRef = useRef(null);

    const debouncedSetSearchText = useMemo(
        () => debounce((value) => {
            setSearchText(value);
            setCurrentPage(0);
        }, 300),
        []
    );

    useEffect(() => {
        return () => {
            debouncedSetSearchText.cancel();
        };
    }, [debouncedSetSearchText]);

    useEffect(() => {
        const load = async () => {
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;

            if (!isInitialLoad) {
                setIsRefetching(true);
            }

            try {
                const token = await getToken();
                const result = await getBookingsForParkingArea(
                    token,
                    parkingAreaExtId,
                    {
                        page: currentPage + 1, // service är 1-baserat
                        pageSize: PAGE_SIZE,
                        search: searchText,
                        includeExpired,
                    },
                    { signal: controller.signal }
                );

                if (controller.signal.aborted) {
                    return;
                }

                if (result && !result.error) {
                    setPagedResult({
                        items: result.items || [],
                        totalPages: result.totalPages || 0,
                        totalCount: result.totalCount || 0,
                    });
                } else if (result?.error) {
                    console.error(result);
                }
            } catch (error) {
                if (error?.name !== 'AbortError') {
                    console.error(error);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setIsInitialLoad(false);
                    setIsRefetching(false);
                }
            }
        };

        load();

        return () => {
            abortRef.current?.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, searchText, includeExpired, parkingAreaExtId]);

    const handleSearchChange = (value) => {
        setSearchInputValue(value);
        debouncedSetSearchText(value);
    };

    const handleFilterBookings = (option) => {
        setFilterBookingOptions((prev) => prev.map((opt) =>
            opt.value === option.value ? { ...opt, isChecked: !opt.isChecked } : opt
        ));
        if (option.value === 'expired') {
            setIncludeExpired((prev) => !prev);
            setCurrentPage(0); // FIX 5: nollställ sida vid filter-toggle
        }
    };

    if (isInitialLoad) {
        return <WidgetLoadingSkeleton />;
    }

    return (
        <>
            <Widget className="mb-6">
                <div className="flex flex-col">
                    <h2 className="text-xl font-medium mb-1">{t('bookingsPage.title')}</h2>
                    <span>{t('bookingsPage.description')}</span>
                </div>
            </Widget>

            <Widget>
                <div className="flex flex-row justify-end ">
                    <div className="flex items-center gap-3">
                        <div className="flex flex-col md:flex-row gap-3">
                            <InputSearch
                                placeHolder={t('generic.search')}
                                value={searchInputValue}
                                onChange={handleSearchChange}
                            />
                            <DropDownMultiSelector
                                options={filterBookingOptions}
                                onOptionSelected={(option) => { handleFilterBookings(option); }}
                            />
                        </div>
                    </div>
                </div>
                <div
                    className="-mr-6 -ml-6 airpark-scroll overflow-x-scroll"
                    style={{ opacity: isRefetching ? 0.6 : 1, transition: 'opacity 150ms' }}
                >
                    <table className="table-auto mt-3 w-full">
                        <thead>
                            <tr className="text-left bg-airpark-gray-300 h-11">
                                <th className="pl-6 whitespace-nowrap">{t('generic.id')}</th>
                                <th className="pl-6 whitespace-nowrap">{t('bookingsPage.parkingSpot')}</th>
                                <th className="pl-6 whitespace-nowrap">{t('bookingsPage.startDate')}</th>
                                <th className="pl-6 whitespace-nowrap">{t('bookingsPage.endDate')}</th>
                                <th className="pl-6 whitespace-nowrap">{t('bookingsPage.duration')}</th>
                                <th className="pl-6 whitespace-nowrap">{t('bookingsPage.paidAmount')}</th>
                                <th className="pl-6 whitespace-nowrap">{t('bookingsPage.registrationIdentifier')}</th>
                                <th className="pl-6 pr-6 whitespace-nowrap">{t('discountCodes.discountCodeColumn')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pagedResult.items.map((item, i) => (
                                <BookingItem key={item.bookingExtId ?? i} booking={item} />
                            ))}
                        </tbody>
                    </table>
                    {pagedResult.items.length === 0 &&
                        <div className="flex w-full flex-row items-center justify-center mt-3 mb-3">
                            <span className="p-3">{t('bookingsPage.noResult')}</span>
                        </div>
                    }
                </div>
                <div className="p-6">
                    <Paging
                        totalNumberOfPages={pagedResult.totalPages}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                    />
                </div>
            </Widget>
        </>
    );
};

const getHoursAndMinutesBetweenDates = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const diff = end.getTime() - start.getTime();

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff - (hours * 1000 * 60 * 60)) / (1000 * 60));

    return `${hours}h ${minutes}m`;
}

const BookingItem = ({ booking }) => {
    return (
        <tr className="h-16 border-b-[1px] border-airpark-gray-300">
            <td className="pl-6">{booking.bookingExtId}</td>
            <td className="pl-6">{booking.spotIdentifier}</td>
            <td className="pl-6 whitespace-nowrap">{booking.startDateTime.substring(0, 10)} {booking.startDateTime.substring(11, 16)}</td>
            <td className="pl-6 whitespace-nowrap">{booking.endDateTime.substring(0, 10)} {booking.endDateTime.substring(11, 16)}</td>
            <td className="pl-6 whitespace-nowrap">{getHoursAndMinutesBetweenDates(booking.startDateTime, booking.endDateTime)}</td>
            <td className="pl-6 whitespace-nowrap">{booking.friendlyPrice}</td>
            <td className="pl-6">{booking.registrationIdentifier}</td>
            <td className="pl-6 pr-6">{booking.discountCode || '-'}</td>
        </tr>
    )
}

export default BookingsPage;
